import { HttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { BlockedShipmentsPage, Shipment, ShipmentsPage } from '../models/Shipments';

const ALLOWED_IMPORT_EXTENSIONS = ['.xlsx', '.xls', '.xlsm', '.xltx', '.xltm'];
const MAX_IMPORT_FILE_BYTES = 10 * 1024 * 1024;

function parseFilenameFromContentDisposition(contentDisposition: string, fallback: string): string {
  if (!contentDisposition) {
    return fallback;
  }
  const utf8 = contentDisposition.match(/filename\*=UTF-8''([^;\s]+)/i);
  if (utf8?.[1]) {
    return decodeURIComponent(utf8[1]);
  }
  const simple = contentDisposition.match(/filename="?([^";\n]+)"?/i);
  return simple?.[1] ?? fallback;
}

async function parseBlobErrorMessage(data: Blob, contentType: string): Promise<string | undefined> {
  if (!contentType.includes('application/json')) {
    return undefined;
  }
  try {
    const text = await data.text();
    const parsed = JSON.parse(text) as { message?: string };
    return parsed.message;
  } catch {
    return undefined;
  }
}

export interface ShipmentsFilters {
  searchFilter?: { filters: any[]; logic: string };
  search?: string; // 🔍 Parámetro simple de búsqueda (formato: ?search=valor)
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  financial_status?: string;
  team_id?: number;
  shop_id?: number;
  /** IDs de transportista (coma-separados en query) */
  courier_ids?: number[];
  /** Periodo: MONTHLY, WEEKLY o MONTHLY,WEEKLY */
  period?: string;
  /** IDs de equipo (coma-separados en query) */
  team_ids?: number[];
  /** IDs de tienda (coma-separados en query) */
  shop_ids?: number[];
  /** IDs de método de pago (coma-separados en query) */
  payment_method_ids?: number[];
  /** IDs de estado del pedido (coma-separados en query) */
  status_ids?: number[];
  /** IDs de estado de envío (coma-separados en query) */
  shipment_status_ids?: number[];
  /** IDs de prefacturas (coma-separados en query) */
  pre_invoice_ids?: number[];
  /** IDs de facturas (coma-separados en query) */
  invoice_ids?: number[];
}

/** Payload para POST /invoices/pre-invoices/shipments */
export interface PreInvoiceShipmentsPayload {
  shipment_ids?: number[];
  advanced_filters?: { filters: any[]; logic: string };
  courier_ids?: number[];
  period?: string;
  team_ids?: number[];
  shop_ids?: number[];
  payment_method_ids?: number[];
  status_ids?: number[];
  shipment_status_ids?: number[];
}

// Construye los parámetros de advanced_filters para la API
function buildAdvancedFiltersParams(searchFilter: { filters: any[]; logic: string }) {
  const params: Record<string, any> = {};
  if (searchFilter.filters && Array.isArray(searchFilter.filters)) {
    searchFilter.filters.forEach((f, i) => {
      params[`advanced_filters[filters][${i}][column]`] = f.column;
      params[`advanced_filters[filters][${i}][operator]`] = f.operator;
      // Solo enviar value si existe (para operadores is_null / is_not_null no debe enviarse)
      if (f.value !== undefined && f.value !== null && !(Array.isArray(f.value) && f.value.length === 0)) {
        params[`advanced_filters[filters][${i}][value]`] = f.value;
      }
    });
  }
  if (searchFilter.logic) {
    params['advanced_filters[logic]'] = searchFilter.logic;
  }
  return params;
}

export class ShipmentsApiAdapter {
  constructor(private httpClient: HttpClient) {}

  /**
   * Obtiene los shipments elegibles para facturación de forma paginada
   * Incluye datos relacionados: order, customer, shop, courier, estados
   */
  async getShipmentsPaginated(filters: ShipmentsFilters = {}): Promise<ShipmentsPage> {
    const {
      searchFilter,
      search, // 🔍 Parámetro simple de búsqueda
      sortBy = 'id',
      sortDirection = 'desc',
      page = 1,
      limit = 25,
      financial_status,
      team_id,
      shop_id,
      courier_ids,
      period,
      team_ids,
      shop_ids,
      payment_method_ids,
      status_ids,
      shipment_status_ids,
      pre_invoice_ids,
      invoice_ids,
    } = filters;

    const toCommaSeparated = (arr: number[] | undefined) => (arr && arr.length > 0 ? arr.join(',') : undefined);

    const params: any = {
      ...(sortBy && { 'sorting[column]': sortBy }),
      ...(sortDirection && { 'sorting[direction]': sortDirection }),
      page,
      per_page: limit,
      ...(financial_status && { financial_status }),
      ...(team_id && { team_id }),
      ...(shop_id && { shop_id }),
      ...(search && { search }), // 🔍 Búsqueda simple: ?search=valor
      ...(toCommaSeparated(courier_ids) && { courier_ids: toCommaSeparated(courier_ids) }),
      ...(period && { period }),
      ...(toCommaSeparated(team_ids) && { team_ids: toCommaSeparated(team_ids) }),
      ...(toCommaSeparated(shop_ids) && { shop_ids: toCommaSeparated(shop_ids) }),
      ...(toCommaSeparated(payment_method_ids) && {
        payment_method_ids: toCommaSeparated(payment_method_ids),
      }),
      ...(toCommaSeparated(status_ids) && { status_ids: toCommaSeparated(status_ids) }),
      ...(toCommaSeparated(shipment_status_ids) && {
        shipment_status_ids: toCommaSeparated(shipment_status_ids),
      }),
      ...(toCommaSeparated(pre_invoice_ids) && {
        pre_invoice_ids: toCommaSeparated(pre_invoice_ids),
      }),
      ...(toCommaSeparated(invoice_ids) && {
        invoice_ids: toCommaSeparated(invoice_ids),
      }),
      ...(searchFilter ? buildAdvancedFiltersParams(searchFilter) : {}),
    };

    // Solo mostrar loading global para la primera página
    const showGlobalLoading = page === 1;
    const response = await this.httpClient.get<any>('/invoices/shipments/eligible', params, {}, showGlobalLoading);

    return {
      shipments: response.data,
      nextPage: response.meta.currentPage < response.meta.lastPage ? response.meta.currentPage + 1 : undefined,
      hasMore: response.meta.currentPage < response.meta.lastPage,
      total: response.meta.total,
    };
  }

  /**
   * Exporta shipments elegibles a Excel
   * POST /api/v1/invoices/shipments/eligible/export
   * Body: { columns: string[] }
   * Query params: search, courier_ids, period, team_ids, shop_ids, etc. (igual que GET eligible)
   * @returns { url_file: string } URL de descarga del Excel generado
   */
  async exportEligibleShipments(filters: ShipmentsFilters = {}, columns: string[] = []): Promise<{ url_file: string }> {
    const {
      searchFilter,
      search,
      sortBy = 'id',
      sortDirection = 'desc',
      financial_status,
      team_id,
      shop_id,
      courier_ids,
      period,
      team_ids,
      shop_ids,
      payment_method_ids,
      status_ids,
      shipment_status_ids,
      pre_invoice_ids,
      invoice_ids,
    } = filters;

    const toCommaSeparated = (arr: number[] | undefined) => (arr && arr.length > 0 ? arr.join(',') : undefined);

    const params: any = {
      ...(sortBy && { 'sorting[column]': sortBy }),
      ...(sortDirection && { 'sorting[direction]': sortDirection }),
      ...(financial_status && { financial_status }),
      ...(team_id && { team_id }),
      ...(shop_id && { shop_id }),
      ...(search && { search }),
      ...(toCommaSeparated(courier_ids) && { courier_ids: toCommaSeparated(courier_ids) }),
      ...(period && { period }),
      ...(toCommaSeparated(team_ids) && { team_ids: toCommaSeparated(team_ids) }),
      ...(toCommaSeparated(shop_ids) && { shop_ids: toCommaSeparated(shop_ids) }),
      ...(toCommaSeparated(payment_method_ids) && {
        payment_method_ids: toCommaSeparated(payment_method_ids),
      }),
      ...(toCommaSeparated(status_ids) && { status_ids: toCommaSeparated(status_ids) }),
      ...(toCommaSeparated(shipment_status_ids) && {
        shipment_status_ids: toCommaSeparated(shipment_status_ids),
      }),
      ...(toCommaSeparated(pre_invoice_ids) && {
        pre_invoice_ids: toCommaSeparated(pre_invoice_ids),
      }),
      ...(toCommaSeparated(invoice_ids) && {
        invoice_ids: toCommaSeparated(invoice_ids),
      }),
      ...(searchFilter ? buildAdvancedFiltersParams(searchFilter) : {}),
    };

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        const strValue = Array.isArray(value) ? value.join(',') : String(value);
        searchParams.append(key, strValue);
      }
    });
    const queryString = searchParams.toString();

    const url = `/invoices/shipments/eligible/export${queryString ? `?${queryString}` : ''}`;

    const defaultColumns = [
      'id',
      'ref',
      'shop_name',
      'team_name',
      'courier_name',
      'country',
      'shipping_date',
      'financial_status',
    ];

    const body = {
      columns: columns.length > 0 ? columns : defaultColumns,
    };

    const response = await this.httpClient.post<{ url_file: string }>(url, body);
    return response;
  }

  /**
   * Obtiene todos los shipments elegibles (primera página)
   */
  async getAll(): Promise<Shipment[]> {
    const response = await this.getShipmentsPaginated();
    return response.shipments;
  }

  /**
   * Obtiene un shipment específico por su ID
   */
  async getShipmentById(id: number): Promise<Shipment> {
    const response = await this.httpClient.get<Shipment>(`/invoices/shipments/${id}`);
    return response;
  }

  /**
   * Bloquea un shipment
   * @param id Shipment ID
   * @param payload Datos requeridos por la API: shipment_block_reason_id (number|string), comment (string)
   */
  async blockShipment(
    id: number,
    payload?: { shipment_block_reason_id?: number | string; comment?: string },
  ): Promise<any> {
    const response = await this.httpClient.post<any>(`/invoices/shipments/${id}/block`, payload || {});
    return response;
  }

  /**
   * Desbloquea un shipment
   * @param id ID del registro de bloqueo (accounting_shipment_blocks.id), no el shipment_id
   * @returns Shipment desbloqueado con financial_status = NOT_INVOICED
   */
  async unblockShipment(id: number): Promise<any> {
    const response = await this.httpClient.delete<any>(`/invoices/shipments/${id}/block`);
    return response;
  }

  /**
   * Prefactura un shipment (crea factura)
   * @param id Shipment ID
   */
  async invoiceShipment(id: number): Promise<any> {
    const response = await this.httpClient.post<any>(`/invoices/pre-invoices/${id}/invoice`, {});
    return response;
  }

  /**
   * Prefactura múltiples shipments en bloque
   * @param shipmentIds Array de IDs de shipments a prefacturar
   * @param additionalFilters Filtros opcionales adicionales (courier_ids, period, team_ids, etc.)
   * @returns Objeto de pre-factura creada con: id, code, shipments, status, team_id, total_amount, user_id
   */
  async bulkInvoiceShipments(
    shipmentIds: number[],
    additionalFilters?: Omit<PreInvoiceShipmentsPayload, 'shipment_ids' | 'advanced_filters'>,
  ): Promise<any> {
    const body: PreInvoiceShipmentsPayload = {
      shipment_ids: shipmentIds,
      ...additionalFilters,
    };
    const response = await this.httpClient.post<any>(`/invoices/pre-invoices/shipments`, body);
    return response;
  }

  /**
   * Prefactura shipments usando filtros avanzados
   * @param advancedFilters Filtros avanzados con la estructura { filters: [], logic: 'AND'|'OR' }
   * @param additionalFilters Filtros opcionales adicionales (courier_ids, period, team_ids, etc.)
   * @returns Objeto de pre-factura creada
   */
  async bulkInvoiceShipmentsByFilters(
    advancedFilters: { filters: any[]; logic: string },
    additionalFilters?: Omit<PreInvoiceShipmentsPayload, 'shipment_ids' | 'advanced_filters'>,
  ): Promise<any> {
    const body: PreInvoiceShipmentsPayload = {
      advanced_filters: advancedFilters,
      ...additionalFilters,
    };
    const response = await this.httpClient.post<any>(`/invoices/pre-invoices/shipments`, body);
    return response;
  }

  /**
   * Prefactura shipments con payload completo (shipment_ids y/o advanced_filters + filtros)
   * POST /api/v1/invoices/pre-invoices/shipments
   */
  async bulkInvoiceShipmentsWithPayload(payload: PreInvoiceShipmentsPayload): Promise<any> {
    const response = await this.httpClient.post<any>(`/invoices/pre-invoices/shipments`, payload);
    return response;
  }

  /**
   * Actualiza un shipment
   * @param id Shipment ID
   * @param payload Datos a actualizar: weight_internal_kg, num_items, lines (solo base_amount)
   * @returns Shipment actualizado
   */
  async updateShipment(
    id: number,
    payload: {
      weight_internal_kg?: number;
      num_items?: number;
      lines?: Array<{
        id: number;
        base_amount: number;
      }>;
    },
  ): Promise<any> {
    const response = await this.httpClient.put<any>(`/invoices/shipments/${id}`, payload);
    return response;
  }

  /**
   * Actualiza múltiples shipments en una sola llamada (endpoint bulk)
   * PUT /api/v1/invoices/shipments/bulk
   * @param shipments Array de shipments con sus datos a actualizar
   * @returns Resultados de la actualización masiva
   */
  async bulkUpdateShipments(
    shipments: Array<{
      id: number;
      weight_internal_kg?: number;
      num_items?: number;
      lines?: Array<{
        id: number;
        base_amount: number;
      }>;
    }>,
  ): Promise<{
    total: number;
    successful: number;
    failed: number;
    results: Array<{
      shipment_id: number;
      success: boolean;
      message: string;
      data: any;
    }>;
  }> {
    const response = await this.httpClient.put<any>(`/invoices/shipments/bulk`, { shipments });
    return response;
  }

  /**
   * Calcula las líneas de un shipment basándose en peso y número de items
   * POST /api/v1/invoices/shipments/calculate-lines
   * @param orderId Order ID
   * @param weight Peso en kg
   * @param numItems Número de items
   * @returns Respuesta con líneas calculadas y resumen
   */
  async calculateLines(
    orderId: number,
    weight: number,
    numItems: number,
  ): Promise<{
    order_id: number;
    weight_kg: number;
    lines: Array<{
      concept: string;
      base_amount: number;
      total_amount: number;
      taxes: any;
    }>;
    summary: {
      total_base_amount: number;
      total_taxes: number;
      total_amount: number;
    };
  }> {
    const response = await this.httpClient.post<any>(`/invoices/shipments/calculate-lines`, {
      order_id: orderId,
      weight: weight,
      num_items: numItems,
    });
    return response;
  }

  /**
   * Obtiene los shipments bloqueados de forma paginada
   * GET /api/v1/invoices/shipments/block
   * Incluye razón, comentario y usuario que lo bloqueó
   */
  async getBlockedShipmentsPaginated(filters: ShipmentsFilters = {}): Promise<BlockedShipmentsPage> {
    const {
      searchFilter,
      search,
      sortBy = 'id',
      sortDirection = 'desc',
      page = 1,
      limit = 25,
      courier_ids,
      period,
      team_ids,
      shop_ids,
      payment_method_ids,
      status_ids,
      shipment_status_ids,
    } = filters;

    const toCommaSeparated = (arr: number[] | undefined) => (arr && arr.length > 0 ? arr.join(',') : undefined);

    const params: any = {
      ...(sortBy && { 'sorting[column]': sortBy }),
      ...(sortDirection && { 'sorting[direction]': sortDirection }),
      page,
      per_page: limit,
      ...(search && { search }),
      ...(toCommaSeparated(courier_ids) && { courier_ids: toCommaSeparated(courier_ids) }),
      ...(period && { period }),
      ...(toCommaSeparated(team_ids) && { team_ids: toCommaSeparated(team_ids) }),
      ...(toCommaSeparated(shop_ids) && { shop_ids: toCommaSeparated(shop_ids) }),
      ...(toCommaSeparated(payment_method_ids) && {
        payment_method_ids: toCommaSeparated(payment_method_ids),
      }),
      ...(toCommaSeparated(status_ids) && { status_ids: toCommaSeparated(status_ids) }),
      ...(toCommaSeparated(shipment_status_ids) && {
        shipment_status_ids: toCommaSeparated(shipment_status_ids),
      }),
      ...(searchFilter ? buildAdvancedFiltersParams(searchFilter) : {}),
    };

    // Solo mostrar loading global para la primera página
    const showGlobalLoading = page === 1;
    const response = await this.httpClient.get<any>('/invoices/shipments/block', params, {}, showGlobalLoading);

    return {
      blockedShipments: response.data,
      nextPage: response.meta.currentPage < response.meta.lastPage ? response.meta.currentPage + 1 : undefined,
      hasMore: response.meta.currentPage < response.meta.lastPage,
      total: response.meta.total,
    };
  }

  /**
   * Importa accounting shipments desde Excel
   * POST /api/v1/invoices/shipments/import
   * Respuesta éxito: archivo .xlsx (blob)
   * Respuesta error: JSON
   */
  async importAccountingShipments(file: File): Promise<{ blob: Blob; filename: string }> {
    const ext = file.name.includes('.') ? file.name.slice(file.name.lastIndexOf('.')).toLowerCase() : '';
    if (!ALLOWED_IMPORT_EXTENSIONS.includes(ext)) {
      throw new Error('INVALID_FILE_TYPE');
    }
    if (file.size > MAX_IMPORT_FILE_BYTES) {
      throw new Error('FILE_TOO_LARGE');
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await apiClientWH.postForm<Blob>('/invoices/shipments/import', formData, {
        responseType: 'blob',
      });

      const contentDisposition = (response.headers['content-disposition'] as string) || '';
      const filename = parseFilenameFromContentDisposition(contentDisposition, 'accounting_shipments_import.xlsx');

      return { blob: response.data, filename };
    } catch (error: any) {
      const responseData = error?.response?.data;
      if (responseData instanceof Blob) {
        const contentType = (error.response.headers?.['content-type'] as string) || '';
        const message = await parseBlobErrorMessage(responseData, contentType);
        if (message) {
          throw new Error(message);
        }
      }
      throw error;
    }
  }
}
